/**
 * Aviso de que el proyecto tiene planos, con el interruptor para leerlos o no.
 *
 * Vive en la pestaña RFI (Junta de aclaraciones), que —junto con la cotización—
 * es donde los planos se LEEN. El resumen y Documentos solo los detectan, no los
 * usan, así que ahí no va (era ruido en pantallas que no los consumen).
 *
 * Viene encendido. Leer un pliego completo de láminas cuesta centavos, así que
 * pedir permiso de antemano sería fricción sin motivo; se avisa —incluido que
 * consume tokens— y se puede apagar.
 */
import { useState } from 'react';
import { makeStyles, tokens, Switch, Spinner } from '@fluentui/react-components';
import { useEnCreditos } from '../lib/useEnCreditos.js';
import { DocumentLandscape24Regular } from '@fluentui/react-icons';
import { patchProject, type Project } from '../api/projects.js';
import { OCULTAR_COSTO_IA } from '../lib/flags.js';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    gap: '10px',
    padding: '12px 14px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: 'var(--bg-surface-alt, ' + tokens.colorNeutralBackground2 + ')',
  },
  icon: { color: tokens.colorBrandForeground1, flexShrink: 0, marginTop: '2px' },
  body: { display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0, flex: 1 },
  title: { fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' },
  text: { fontSize: '12px', lineHeight: '17px', color: 'var(--text-secondary)' },
  row: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  staff: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
  },
  err: { fontSize: '12px', color: tokens.colorPaletteRedForeground1 },
});

interface Props {
  project: Project;
  /** Láminas que todavía no se leyeron. 0 = ya está todo leído y no vuelve a consumir. */
  pendientes?: number;
  /** Costo estimado en dólares de leer lo que falta. Solo llega si es staff Ecosoft. */
  costoUsd?: number | null;
  /** Falso en el proyecto de ejemplo o sin permiso de escritura. */
  canEdit?: boolean;
  /** El proyecto cambió → el padre refresca. */
  onChanged?: (p: Project) => void;
}

export function PlanosNotice({ project, pendientes, costoUsd, canEdit = true, onChanged }: Props) {
  const enCr = useEnCreditos();
  const styles = useStyles();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [leer, setLeer] = useState(project.leerPlanos !== false);

  const laminas = project.planosLaminas ?? 0;
  if (laminas === 0) return null;
  // Sin dato del servidor se asume que falta leer: es el aviso más prudente.
  const porLeer = pendientes ?? laminas;

  async function cambiar(valor: boolean) {
    setLeer(valor);
    setSaving(true);
    setErr('');
    try {
      const p = await patchProject(project.id, { leerPlanos: valor });
      onChanged?.(p);
    } catch (e) {
      setLeer(!valor);
      setErr(e instanceof Error ? e.message : 'No se pudo guardar el cambio');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.root}>
      <DocumentLandscape24Regular className={styles.icon} fontSize={18} />
      <div className={styles.body}>
        <span className={styles.title}>
          {/* El resumen ya trae el conteo ("18 hojas (7 en A3, …)"): repetirlo acá
              dejaba "18 láminas: 18 láminas (…)". */}
          Este proyecto tiene planos
          {project.planosResumen
            ? `: ${project.planosResumen}`
            : ` (${laminas} ${laminas === 1 ? 'hoja' : 'hojas'})`}
        </span>
        <span className={styles.text}>
          {porLeer > 0 ? (
            <>
              Podemos leerlos para tomar medidas, materiales y cantidades que el catálogo
              no dice. Se leen <strong>una sola vez</strong> (consume {enCr ? 'créditos' : 'tokens'} de tu bolsa) y
              quedan guardados
              {porLeer < laminas
                ? ` — quedan ${porLeer} ${porLeer === 1 ? 'hoja' : 'hojas'} por leer`
                : ''}.
            </>
          ) : (
            <>Ya se leyeron y quedaron guardados: usarlos no vuelve a consumir créditos.</>
          )}
        </span>
        <div className={styles.row}>
          <Switch
            checked={leer}
            disabled={!canEdit || saving}
            onChange={(_, d) => void cambiar(d.checked)}
            label={leer ? 'Leer los planos de esta obra' : 'No leer los planos'}
          />
          {saving && <Spinner size="extra-tiny" />}
        </div>
        {/* Solo staff Ecosoft: al cliente nunca se le muestra el costo. */}
        {costoUsd != null && !OCULTAR_COSTO_IA && (
          <span className={styles.staff}>
            staff · leer {porLeer === 1 ? 'la hoja que falta' : `las ${porLeer} hojas que faltan`} ≈ USD {costoUsd.toFixed(4)}, una sola vez
          </span>
        )}
        {err && <span className={styles.err}>{err}</span>}
      </div>
    </div>
  );
}
